import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import NotificationBell from '@/components/NotificationBell';

const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/28fc0be7-4196-4b45-b5b2-7034b2e56bae/images/d88baf08cf9aab8cd54854b5018978ec360b4e238c0fbb7dabe14674d502f36a.png';

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 glass-header" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={LOGO_URL} alt="Nest Geneva" className="h-10 w-auto" />
            <span className="font-heading text-lg font-semibold text-[#0B3D2E] hidden sm:block">Nest Geneva</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {!user && (
              <a href="/#how-it-works" className="text-sm text-[#2B2B2B] hover:text-[#0B3D2E] transition-colors" data-testid="nav-how-it-works">
                {t('nav.howItWorks')}
              </a>
            )}
            {user && user.role === 'tenant' && (
              <>
                <Link to="/dashboard" className="text-sm text-[#2B2B2B] hover:text-[#0B3D2E] transition-colors" data-testid="nav-dashboard">
                  {t('nav.dashboard')}
                </Link>
                <Link to="/application" className="text-sm text-[#2B2B2B] hover:text-[#0B3D2E] transition-colors" data-testid="nav-application">
                  {t('nav.application')}
                </Link>
              </>
            )}
            {user && user.role === 'admin' && (
              <Link to="/admin" className="text-sm text-[#2B2B2B] hover:text-[#0B3D2E] transition-colors" data-testid="nav-admin">
                {t('nav.admin')}
              </Link>
            )}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Notifications bell (only renders when authenticated) */}
            {user && <NotificationBell />}

            {/* Language Switcher */}
            <button
              onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
              className="flex items-center gap-1 text-xs font-medium text-[#5a5a5a] hover:text-[#0B3D2E] transition-colors px-2 py-1 rounded"
              data-testid="language-switcher"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === 'en' ? 'FR' : 'EN'}
            </button>

            {/* Auth buttons */}
            {!user ? (
              <div className="hidden md:flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-sm text-[#2B2B2B]" data-testid="nav-login-btn">
                  {t('nav.login')}
                </Button>
                <Button onClick={() => navigate('/application')} className="text-sm bg-[#0B3D2E] hover:bg-[#0f523e] text-white" data-testid="nav-register-btn">
                  {t('nav.register')}
                </Button>
              </div>
            ) : (
              <div className="hidden md:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="text-sm gap-1 max-w-[220px]" data-testid="user-menu-trigger">
                      <span className="truncate">{user.name || user.email}</span> <ChevronDown className="w-3 h-3 shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {user.role === 'tenant' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">{t('nav.dashboard')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/dashboard?tab=account')} data-testid="menu-account">{t('dash.account')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/application')} data-testid="menu-application">{t('nav.application')}</DropdownMenuItem>
                      </>
                    )}
                    {user.role === 'admin' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">{t('nav.admin')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin?tab=users')} data-testid="menu-admin-users">{t('admin.users')}</DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">{t('nav.logout')}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Mobile burger */}
            <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-black/5 bg-white animate-fade-in" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-2">
            {!user ? (
              <>
                <a href="/#how-it-works" className="block py-2 text-sm text-[#2B2B2B]" onClick={() => setMobileOpen(false)}>{t('nav.howItWorks')}</a>
                <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/login'); setMobileOpen(false); }}>{t('nav.login')}</Button>
                <Button className="w-full bg-[#0B3D2E] text-white text-sm" onClick={() => { navigate('/application'); setMobileOpen(false); }}>{t('nav.register')}</Button>
              </>
            ) : (
              <>
                {user.role === 'tenant' && (
                  <>
                    <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}>{t('nav.dashboard')}</Button>
                    <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/dashboard?tab=account'); setMobileOpen(false); }}>{t('dash.account')}</Button>
                    <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/application'); setMobileOpen(false); }}>{t('nav.application')}</Button>
                  </>
                )}
                {user.role === 'admin' && (
                  <>
                    <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/admin'); setMobileOpen(false); }}>{t('nav.admin')}</Button>
                    <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/admin?tab=users'); setMobileOpen(false); }}>{t('admin.users')}</Button>
                  </>
                )}
                <Button variant="ghost" className="w-full justify-start text-sm text-red-600" onClick={() => { handleLogout(); setMobileOpen(false); }}>{t('nav.logout')}</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
